import axios from 'axios'

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
})

// ── Auth token interceptor ─────────────────────────────────────
api.interceptors.request.use(config => {
  const token = localStorage.getItem('auth_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// ── 401 → logout ──────────────────────────────────────────────
api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth_token')
      window.location.href = '/admin/login'
    }
    return Promise.reject(err)
  }
)

// ══════════════════════════════════════════════════════════════
// AUTH
// ══════════════════════════════════════════════════════════════
export const authApi = {
  login:           (data) => api.post('/auth/login', data),
  verify2fa:       (data) => api.post('/auth/verify-2fa', data),
  logout:          ()     => api.post('/auth/logout'),
  me:              ()     => api.get('/auth/me'),
  changePassword:  (data) => api.put('/auth/change-password', data),
}

// ══════════════════════════════════════════════════════════════
// SCREENS
// ══════════════════════════════════════════════════════════════
export const screensApi = {
  list:            (params) => api.get('/admin/screens', { params }),
  get:             (id)     => api.get(`/admin/screens/${id}`),
  update:          (id, d)  => api.put(`/admin/screens/${id}`, d),
  delete:          (id)     => api.delete(`/admin/screens/${id}`),
  authorize:       (id)     => api.post(`/admin/screens/${id}/authorize`),
  requestScreenshot: (id)   => api.post(`/admin/screens/${id}/screenshot`),
  sendPriority:    (id, d)  => api.post(`/admin/screens/${id}/priority`, d),
  attachGroups:    (id, d)  => api.post(`/admin/screens/${id}/groups`, d),
  getScreenshots:  (id)     => api.get(`/admin/screens/${id}/screenshots`),
  getSensors:      (id)     => api.get(`/admin/screens/${id}/sensors`),
}

// ══════════════════════════════════════════════════════════════
// SCREEN GROUPS
// ══════════════════════════════════════════════════════════════
export const groupsApi = {
  list:   ()       => api.get('/admin/screen-groups'),
  create: (data)   => api.post('/admin/screen-groups', data),
  update: (id, d)  => api.put(`/admin/screen-groups/${id}`, d),
  delete: (id)     => api.delete(`/admin/screen-groups/${id}`),
}

// ══════════════════════════════════════════════════════════════
// MEDIA
// ══════════════════════════════════════════════════════════════
export const mediaApi = {
  list:   (params) => api.get('/admin/media', { params }),
  upload: (fd, onProgress) => api.post('/admin/media', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: e => onProgress?.(Math.round((e.loaded * 100) / e.total)),
  }),
  update: (id, d)  => api.put(`/admin/media/${id}`, d),
  delete: (id)     => api.delete(`/admin/media/${id}`),
}

// ══════════════════════════════════════════════════════════════
// PLAYLISTS
// ══════════════════════════════════════════════════════════════
export const playlistsApi = {
  list:   ()         => api.get('/admin/playlists'),
  create: (data)     => api.post('/admin/playlists', data),
  get:    (id)       => api.get(`/admin/playlists/${id}`),
  update: (id, d)    => api.put(`/admin/playlists/${id}`, d),
  delete: (id)       => api.delete(`/admin/playlists/${id}`),
}

// ══════════════════════════════════════════════════════════════
// SCHEDULES
// ══════════════════════════════════════════════════════════════
export const schedulesApi = {
  list:   ()        => api.get('/admin/schedules'),
  create: (data)    => api.post('/admin/schedules', data),
  update: (id, d)   => api.put(`/admin/schedules/${id}`, d),
  delete: (id)      => api.delete(`/admin/schedules/${id}`),
}

// ══════════════════════════════════════════════════════════════
// TICKERS
// ══════════════════════════════════════════════════════════════
export const tickersApi = {
  list:   ()        => api.get('/admin/tickers'),
  create: (data)    => api.post('/admin/tickers', data),
  update: (id, d)   => api.put(`/admin/tickers/${id}`, d),
  delete: (id)      => api.delete(`/admin/tickers/${id}`),
}

// ══════════════════════════════════════════════════════════════
// STREAMS
// ══════════════════════════════════════════════════════════════
export const streamsApi = {
  list:          ()          => api.get('/admin/streams'),
  create:        (data)      => api.post('/admin/streams', data),
  delete:        (id)        => api.delete(`/admin/streams/${id}`),
  parseM3u:      (id)        => api.post(`/admin/streams/${id}/parse-m3u`),
  selectChannel: (id, index) => api.post(`/admin/streams/${id}/select-channel`, { index }),
}

// ══════════════════════════════════════════════════════════════
// ALERTS
// ══════════════════════════════════════════════════════════════
export const alertsApi = {
  list:       (params) => api.get('/admin/alerts', { params }),
  markSeen:   (id)     => api.post(`/admin/alerts/${id}/seen`),
  markAllSeen: ()      => api.post('/admin/alerts/seen-all'),
}

// ══════════════════════════════════════════════════════════════
// STATISTICS
// ══════════════════════════════════════════════════════════════
export const statsApi = {
  overview:  ()       => api.get('/admin/stats/overview'),
  playback:  (params) => api.get('/admin/stats/playback', { params }),
  screens:   (params) => api.get('/admin/statistics/screens', { params }),
}

// ══════════════════════════════════════════════════════════════
// API KEYS & WEBHOOKS
// ══════════════════════════════════════════════════════════════
export const apiKeysApi = {
  list:   ()        => api.get('/admin/api-keys'),
  create: (data)    => api.post('/admin/api-keys', data),
  delete: (id)      => api.delete(`/admin/api-keys/${id}`),
}
export const webhooksApi = {
  list:   ()        => api.get('/admin/webhooks'),
  create: (data)    => api.post('/admin/webhooks', data),
  update: (id, d)   => api.put(`/admin/webhooks/${id}`, d),
  delete: (id)      => api.delete(`/admin/webhooks/${id}`),
}

// ══════════════════════════════════════════════════════════════
// SMTP
// ══════════════════════════════════════════════════════════════
export const smtpApi = {
  get:    ()        => api.get('/admin/smtp'),
  update: (data)    => api.put('/admin/smtp', data),
  test:   ()        => api.post('/admin/smtp/test'),
}

// ══════════════════════════════════════════════════════════════
// SUPERADMIN
// ══════════════════════════════════════════════════════════════
export const superadminApi = {
  tenants:  { list: () => api.get('/admin/superadmin/tenants'), create: d => api.post('/admin/superadmin/tenants', d), update: (id,d) => api.put(`/admin/superadmin/tenants/${id}`, d), delete: id => api.delete(`/admin/superadmin/tenants/${id}`) },
  users:    { list: () => api.get('/admin/superadmin/users'), create: d => api.post('/admin/superadmin/users', d), delete: id => api.delete(`/admin/superadmin/users/${id}`) },
  stats:    () => api.get('/admin/superadmin/system-stats'),
  auditLog: (params) => api.get('/admin/superadmin/audit-log', { params }),
}

export default api


